import { useMutation } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { isWeChatBrowser } from "@/lib/utils/wechat";
import { getWechatAuthUrl } from "@/lib/client/wechat-auth";

interface WechatAuthUrlResponse {
  authUrl: string;
}

interface WechatLoginButtonProps {
  referralCode?: string;
}

export function WechatLoginButton({ referralCode }: WechatLoginButtonProps) {
  const [showOpenInWechatHint, setShowOpenInWechatHint] = useState(false);

  useEffect(() => {
    if (typeof navigator === "undefined") {
      return;
    }

    const ua = navigator.userAgent;
    const isMobileBrowser = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
      ua,
    );

    setShowOpenInWechatHint(isMobileBrowser && !isWeChatBrowser());
  }, []);

  const { mutate, isPending } = useMutation<WechatAuthUrlResponse, Error, void>({
    mutationFn: async () => {
      return await getWechatAuthUrl({
        provider: isWeChatBrowser() ? "official-account" : "open-platform",
        referralCode,
      });
    },
    onSuccess: (data) => {
      // 跳转到微信授权页面
      window.location.href = data.authUrl;
    },
    onError: (error) => {
      toast.error(error.message || "微信登录初始化失败");
    },
  });

  const handleLogin = () => {
    mutate();
  };

  return (
    <div className="space-y-2">
      <Button
        variant="outline"
        className="h-11 w-full border-border/70 bg-background/50 hover:bg-muted/40"
        onClick={handleLogin}
        disabled={isPending}
      >
        {isPending ? (
          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
        ) : (
          <svg
            className="mr-2 h-4 w-4"
            viewBox="0 0 24 24"
            fill="currentColor"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path d="M8.691 2.188C3.891 2.188 0 5.476 0 9.53c0 2.212 1.17 4.203 3.002 5.55a.59.59 0 0 1 .213.665l-.39 1.48c-.019.07-.048.141-.048.213 0 .163.13.295.29.295a.326.326 0 0 0 .167-.054l1.903-1.114a.864.864 0 0 1 .717-.098 10.16 10.16 0 0 0 2.837.403c.276 0 .543-.027.811-.05-.857-2.578.157-4.972 1.932-6.446 1.703-1.415 3.882-1.98 5.853-1.838-.576-3.583-4.196-6.348-8.596-6.348zM5.785 5.991c.642 0 1.162.529 1.162 1.18a1.17 1.17 0 0 1-1.162 1.178A1.17 1.17 0 0 1 4.623 7.17c0-.651.52-1.18 1.162-1.18zm5.813 0c.642 0 1.162.529 1.162 1.18a1.17 1.17 0 0 1-1.162 1.178 1.17 1.17 0 0 1-1.162-1.178c0-.651.52-1.18 1.162-1.18zm5.34 2.867c-1.797-.052-3.746.512-5.28 1.786-1.72 1.428-2.687 3.72-1.78 6.22.942 2.453 3.666 4.229 6.884 4.229.826 0 1.622-.12 2.361-.336a.722.722 0 0 1 .598.082l1.584.926a.272.272 0 0 0 .14.047c.134 0 .24-.111.24-.247 0-.06-.023-.12-.038-.177l-.327-1.233a.582.582 0 0 1-.023-.156.49.49 0 0 1 .201-.398C23.024 18.48 24 16.82 24 14.98c0-3.21-2.931-5.837-6.656-6.088V8.89c-.135-.01-.27-.027-.407-.03zm-2.53 3.274c.535 0 .969.44.969.982a.976.976 0 0 1-.969.983.976.976 0 0 1-.969-.983c0-.542.434-.982.97-.982zm4.844 0c.535 0 .969.44.969.982a.976.976 0 0 1-.969.983.976.976 0 0 1-.969-.983c0-.542.434-.982.969-.982z" />
          </svg>
        )}
        微信登录
      </Button>

      {showOpenInWechatHint ? (
        <p className="text-center text-xs text-muted-foreground">建议在微信中打开网站以快速登录</p>
      ) : null}
    </div>
  );
}
